import { Button, IconButton, Icons, Skeleton } from '@dbrain/ui';
import { createContext, Fragment, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CitationView } from '@dbrain/app-core/conversation-state';
import { parseMarkdown, type Block, type Inline, type PendingCardKind } from '@dbrain/app-core/markdown';
import { fileMentions } from '@dbrain/app-core/workspace-files';

/** DOC-004: an answer's citations; "[3]" in its text becomes a chip that opens the source. */
const Citations = createContext<{ byNumber: Map<number, CitationView>; open: (n: number) => void } | null>(null);
const MARK = /\[(\d{1,4}(?:\s*[,;]\s*\d{1,4})*)\]/g;

/** D-99: names of the workspace's files in an answer become chips that open the file in the panel. */
export interface FileLinks {
  resolve: (name: string) => string | null;
  open: (path: string) => void;
}
const Files = createContext<FileLinks | null>(null);

function FileChip({ path, label }: { path: string; label: string }) {
  const { t } = useTranslation();
  const files = useContext(Files);
  return (
    <button type="button" className="file-chip" title={t('codePanel.openFile', { path })} onClick={() => files?.open(path)} data-testid="file-chip" data-path={path}>
      {label}
    </button>
  );
}

/** Plain text with the workspace's file names as chips (never inside a link). */
function FileText({ text, plain }: { text: string; plain: boolean }) {
  const files = useContext(Files);
  const mentions = files && !plain ? fileMentions(text, files.resolve) : [];
  if (mentions.length === 0) return <>{text}</>;
  const out: ReactNode[] = [];
  let last = 0;
  for (const { at, name, path } of mentions) {
    out.push(text.slice(last, at));
    out.push(<FileChip key={at} path={path} label={name} />);
    last = at + name.length;
  }
  out.push(text.slice(last));
  return <>{out}</>;
}

/** Inline code that names a workspace file is that file's chip. */
function InlineCode({ text, plain }: { text: string; plain: boolean }) {
  const files = useContext(Files);
  const path = files && !plain ? files.resolve(text) : null;
  return path ? <FileChip path={path} label={text.trim()} /> : <code>{text}</code>;
}

/** "1 · s. 7", "4 · satır 12", "2 · slayt 3", "5 · Riskler": where a citation points. */
export function citationLabel(c: Pick<CitationView, 'n' | 'kind' | 'number' | 'label' | 'rowFrom' | 'rowTo'>, t: (key: string, options?: Record<string, unknown>) => string): string {
  const place =
    c.kind === 'page'
      ? t('citations.page', { number: c.number })
      : c.kind === 'slide'
        ? t('citations.slide', { number: c.number })
        : c.kind === 'sheet'
          ? c.rowFrom
            ? t('citations.row', { number: c.rowFrom })
            : (c.label ?? t('citations.sheet'))
          : (c.label ?? t('citations.section', { number: c.number }));
  return `${c.n} · ${place.length > 28 ? `${place.slice(0, 27)}…` : place}`;
}

function CitedText({ text, plain = false }: { text: string; plain?: boolean }) {
  const { t } = useTranslation();
  const context = useContext(Citations);
  if (!context || context.byNumber.size === 0 || !text.includes('[')) return <FileText text={text} plain={plain} />;
  const out: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(MARK)) {
    const numbers = (match[1] as string).split(/[,;]/).map((x) => Number(x.trim()));
    const known = numbers.map((n) => context.byNumber.get(n)).filter((c): c is CitationView => Boolean(c));
    if (known.length === 0) continue;
    const at = match.index ?? 0;
    // the space before a citation stays with the sentence
    out.push(<FileText key={`t${at}`} text={text.slice(last, at).replace(/\s+$/, ' ')} plain={plain} />);
    for (const citation of known) {
      out.push(
        <button
          key={`${at}-${citation.n}`}
          type="button"
          className="citation-chip"
          data-testid="citation-chip"
          data-n={citation.n}
          title={`${citation.documentName}${citation.quote ? ` — “${citation.quote}”` : ''}`}
          onClick={() => context.open(citation.n)}
        >
          {citationLabel(citation, t)}
        </button>,
      );
    }
    last = at + match[0].length;
  }
  if (last === 0) return <FileText text={text} plain={plain} />;
  out.push(<FileText key="rest" text={text.slice(last)} plain={plain} />);
  return <>{out}</>;
}

/**
 * Renders answers and plans from the parsed tree (markdown.ts): React
 * elements only, so model output cannot inject markup. Code blocks keep
 * their layout (game boards, tables of numbers) and have a copy button;
 * links open in a new tab without access to this page.
 */
function renderInline(nodes: Inline[], plain = false): ReactNode[] {
  return nodes.map((node, index) => {
    switch (node.kind) {
      case 'text':
        return <CitedText key={index} text={node.text} plain={plain} />;
      case 'code':
        return <InlineCode key={index} text={node.text} plain={plain} />;
      case 'strong':
        return <strong key={index}>{renderInline(node.children, plain)}</strong>;
      case 'em':
        return <em key={index}>{renderInline(node.children, plain)}</em>;
      case 'del':
        return <del key={index}>{renderInline(node.children, plain)}</del>;
      case 'link':
        // a link's words stay words: no chip (a button) inside it
        return (
          <AnswerLink key={index} href={node.href}>
            {renderInline(node.children, true)}
          </AnswerLink>
        );
      default:
        return null;
    }
  });
}

export function CopyButton({ text, label, iconOnly = false }: { text: string; label?: string; iconOnly?: boolean }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  if (iconOnly) {
    return (
      <IconButton
        size="sm"
        label={copied ? t('markdown.copied') : (label ?? t('markdown.copy'))}
        icon={copied ? <Icons.check size={16} /> : <Icons.copy size={16} />}
        onClick={copy}
        data-testid="answer-copy"
      />
    );
  }
  return (
    <Button size="sm" variant="ghost" className="copy-button" icon={copied ? <Icons.check size={14} /> : <Icons.copy size={14} />} data-testid="copy-button" onClick={copy}>
      {copied ? t('markdown.copied') : (label ?? t('markdown.copy'))}
    </Button>
  );
}

/**
 * Phase 15e (D-197): in a streaming draft, a Microsoft 365 answer card the model is still writing is a quiet
 * placeholder of the card's shape, never its JSON. A final message has no card blocks (the server takes them
 * out and sends the cards beside the text), so anywhere else one draws nothing.
 */
const CardsPending = createContext(false);

/**
 * A link of the text. In a streaming draft only its words (Phase 15e, docs review): the server makes the
 * addresses that came from Microsoft 365 content plain text in the final message, which a draft is not yet.
 */
function AnswerLink({ href, children }: { href: string; children: ReactNode }) {
  if (useContext(CardsPending)) return <span className="markdown__draft-link">{children}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer nofollow">
      {children}
    </a>
  );
}

function CardPlaceholder({ card }: { card: PendingCardKind }) {
  const pending = useContext(CardsPending);
  const { t } = useTranslation();
  if (!pending) return null;
  return (
    <div className={`answer-card answer-card--pending answer-card--pending-${card ?? 'any'}`} role="status" data-testid="answer-card-pending" data-kind={card ?? ''}>
      <span className="ui-visually-hidden">{t('microsoft365.chat.pending')}</span>
      <Skeleton lines={card === 'mail_list' ? 4 : 2} />
      {card === 'calendar' ? <Skeleton block /> : null}
    </div>
  );
}

function CodeBlock({ language, text }: { language: string | null; text: string }) {
  return (
    <div className="code-frame" data-testid="code-block">
      <div className="code-frame__head">
        <span className="muted">{language ?? ''}</span>
        <CopyButton text={text} />
      </div>
      <pre className="code-block">
        <code>{text}</code>
      </pre>
    </div>
  );
}

function renderBlock(block: Block, index: number): ReactNode {
  switch (block.kind) {
    case 'heading': {
      const Tag = `h${Math.min(6, block.level + 2)}` as 'h3' | 'h4' | 'h5' | 'h6';
      return <Tag key={index}>{renderInline(block.children)}</Tag>;
    }
    case 'paragraph':
      return (
        <p key={index}>
          {block.children.map((line, n) => (
            <Fragment key={n}>
              {n > 0 ? <br /> : null}
              {renderInline(line)}
            </Fragment>
          ))}
        </p>
      );
    case 'list': {
      const items = block.items.map((item, n) => (
        <li key={n} className={item.checked === null ? undefined : 'task-item'}>
          {item.checked !== null ? <input type="checkbox" checked={item.checked} readOnly disabled /> : null}
          {renderInline(item.children)}
          {item.sub ? renderBlock(item.sub, 0) : null}
        </li>
      ));
      return block.ordered ? (
        <ol key={index} start={block.start === 1 ? undefined : block.start}>
          {items}
        </ol>
      ) : (
        <ul key={index}>{items}</ul>
      );
    }
    case 'code':
      return <CodeBlock key={index} language={block.language} text={block.text} />;
    case 'table':
      return (
        <div key={index} className="table-frame">
          <table className="md-table" data-testid="md-table">
            <thead>
              <tr>
                {block.head.map((cell, n) => (
                  <th key={n} className={block.align[n] ? `align-${block.align[n]}` : undefined}>
                    {renderInline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, n) => (
                    <td key={n} className={block.align[n] ? `align-${block.align[n]}` : undefined}>
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'quote':
      return <blockquote key={index}>{block.children.map(renderBlock)}</blockquote>;
    case 'rule':
      return <hr key={index} />;
    case 'card-pending':
      return <CardPlaceholder key={index} card={block.card} />;
    default:
      return null;
  }
}

export function Markdown({
  source,
  className,
  citations,
  onCite,
  files,
  cardsPending = false,
}: {
  source: string;
  className?: string;
  citations?: CitationView[];
  onCite?: (n: number) => void;
  /** CompanyCode: the workspace's files named in the text open in the panel */
  files?: FileLinks | null;
  /** a streaming draft: answer cards being written show as placeholders, links as their words (Phase 15e) */
  cardsPending?: boolean;
}) {
  const value = useMemo(
    () => (citations && citations.length > 0 && onCite ? { byNumber: new Map(citations.map((c) => [c.n, c])), open: onCite } : null),
    [citations, onCite],
  );
  let body = <div className={['markdown', className ?? ''].join(' ').trim()}>{parseMarkdown(source).map(renderBlock)}</div>;
  if (files) body = <Files.Provider value={files}>{body}</Files.Provider>;
  if (cardsPending) body = <CardsPending.Provider value>{body}</CardsPending.Provider>;
  return value ? <Citations.Provider value={value}>{body}</Citations.Provider> : body;
}
